import { Button, Checkbox, TextField } from '@rematter/ui-web';
import { Trash2 } from 'lucide-react';

import {
  AttributeDataType,
  CategoryAttributeFormState,
  enumAttributeDataType,
} from '../../../_state/categoryForm.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';

type CategoryAttributeItemProps = {
  attribute: CategoryAttributeFormState;
  index: number;
  onUpdate: (
    index: number,
    field: keyof CategoryAttributeFormState,
    value: string | boolean | string[] | null,
  ) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
};

export const CategoryAttributeItem = ({
  attribute,
  index,
  onUpdate,
  onRemove,
  disabled = false,
}: CategoryAttributeItemProps) => {
  const t = useTranslations();

  return (
    <div className="@CategoryAttributeItem flex gap-4 space-y-3 rounded-md border border-neutral/10 p-4">
      <div className="flex flex-1 flex-col gap-4">
        <div className="flex items-start justify-between gap-2">
          <div className="grid flex-1 grid-cols-[1fr_1fr_110px_40px] gap-3">
            <TextField
              label={t.inventoryView.categoryCreateDialog.fields.attributeName}
              value={attribute.name}
              onChange={(value) => onUpdate(index, 'name', value)}
              placeholder={
                t.inventoryView.categoryCreateDialog.placeholders.attributeName
              }
              isRequired
              isDisabled={disabled}
            />
            <SelectEnum
              label={t.inventoryView.categoryCreateDialog.fields.dataType}
              enumValue={enumAttributeDataType}
              value={attribute.dataType}
              onChange={(key) =>
                onUpdate(index, 'dataType', key as AttributeDataType)
              }
              isDisabled={disabled}
            />
            <div className="mt-6 flex h-[2.5rem] w-[6.75rem] items-center justify-center rounded-md border border-neutral/20 px-3 py-2">
              <Checkbox
                isSelected={attribute.required}
                onChange={(value) => onUpdate(index, 'required', value)}
                isDisabled={disabled}
              >
                {t.inventoryView.categoryCreateDialog.attributes.required}
              </Checkbox>
            </div>
            <Button
              variant="outline"
              size="small"
              color="neutral"
              icon={<Trash2 size={16} />}
              onPress={() => onRemove(index)}
              aria-label={
                t.inventoryView.categoryCreateDialog.attributes.removeAriaLabel
              }
              className="mt-6 min-h-10"
              isDisabled={disabled}
            />
          </div>
        </div>
        <TextField
          label={t.inventoryView.categoryCreateDialog.fields.description}
          value={attribute.description}
          onChange={(value) => onUpdate(index, 'description', value)}
          placeholder={
            t.inventoryView.categoryCreateDialog.placeholders
              .attributeDescription
          }
          isDisabled={disabled}
        />
        {attribute.dataType === enumAttributeDataType.Enum && (
          <TextField
            label={t.inventoryView.categoryCreateDialog.fields.options}
            value={attribute.options?.join(', ') ?? ''}
            onChange={(value) =>
              onUpdate(
                index,
                'options',
                value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              )
            }
            placeholder={
              t.inventoryView.categoryCreateDialog.placeholders.options
            }
            isDisabled={disabled}
          />
        )}
      </div>
    </div>
  );
};
